import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchIndexerStatus,
  fetchIndexerConfig,
  startAllIndexers,
  stopAllIndexers,
  startIndexer,
  stopIndexer,
  updateIndexerConfig,
  fetchDepositDetectionMetrics,
  fetchRecentDeposits,
  fetchDepositStatsByBlockchain,
  fetchMonitoredAddresses,
  reloadAddresses,
  fetchBloomFilterConfig,
  updateBloomFilterConfig,
  reprocessBlock
} from '../api';
import {
  fetchIndexerPerformanceMetrics,
  fetchEndpointMetrics
} from '../api/indexer-performance';
import {
  fetchIndexerHealthStatus,
  fetchBlockchainHealthStatus
} from '../api/indexer-health';

/**
 * Hook for fetching indexer status
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @param forceRefresh Parameter kept for backward compatibility but no longer used
 * @returns Query result with indexer status
 */
export const useIndexerStatus = (refreshInterval = 30000, forceRefresh = false) => {
  // Note: forceRefresh parameter is kept for backward compatibility
  // but the API now always bypasses cache regardless of this value
  return useQuery({
    queryKey: ['indexerStatus'],
    queryFn: () => fetchIndexerStatus(true), // Always force refresh
    refetchInterval: refreshInterval,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for fetching indexer configuration
 * @returns Query result with indexer configuration
 */
export const useIndexerConfig = () => {
  return useQuery({
    queryKey: ['indexerConfig'],
    queryFn: fetchIndexerConfig
  });
};

/**
 * Hook for starting all indexers
 * @returns Mutation for starting all indexers
 */
export const useStartAllIndexers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: startAllIndexers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['indexerStatus'] });
    }
  });
};

/**
 * Hook for stopping all indexers
 * @returns Mutation for stopping all indexers
 */
export const useStopAllIndexers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: stopAllIndexers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['indexerStatus'] });
    }
  });
};

/**
 * Hook for starting a specific indexer
 * @returns Mutation for starting a specific indexer
 */
export const useStartIndexer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: startIndexer,
    onMutate: async (blockchain) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['indexerStatus'] });

      // Get the current status
      const previousStatus = queryClient.getQueryData(['indexerStatus']);

      // Optimistically update the status
      queryClient.setQueryData(['indexerStatus'], (old: any) => {
        if (!old || !old.data) return old;

        // Create a copy of the data
        const newData = [...old.data];

        // Find the indexer and update its status
        const indexerIndex = newData.findIndex(
          (indexer: any) => indexer.blockchain.toLowerCase() === blockchain.toLowerCase()
        );

        if (indexerIndex !== -1) {
          newData[indexerIndex] = {
            ...newData[indexerIndex],
            status: 'running'
          };
        }

        return { ...old, data: newData };
      });

      return { previousStatus };
    },
    onError: (err, blockchain, context) => {
      // If the mutation fails, revert back to the previous status
      if (context?.previousStatus) {
        queryClient.setQueryData(['indexerStatus'], context.previousStatus);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the correct data
      queryClient.invalidateQueries({ queryKey: ['indexerStatus'] });
    }
  });
};

/**
 * Hook for stopping a specific indexer
 * @returns Mutation for stopping a specific indexer
 */
export const useStopIndexer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: stopIndexer,
    onMutate: async (blockchain) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['indexerStatus'] });

      // Get the current status
      const previousStatus = queryClient.getQueryData(['indexerStatus']);

      // Optimistically update the status
      queryClient.setQueryData(['indexerStatus'], (old: any) => {
        if (!old || !old.data) return old;

        // Create a copy of the data
        const newData = [...old.data];

        // Find the indexer and update its status
        const indexerIndex = newData.findIndex(
          (indexer: any) => indexer.blockchain.toLowerCase() === blockchain.toLowerCase()
        );

        if (indexerIndex !== -1) {
          newData[indexerIndex] = {
            ...newData[indexerIndex],
            status: 'stopped'
          };
        }

        return { ...old, data: newData };
      });

      return { previousStatus };
    },
    onError: (err, blockchain, context) => {
      // If the mutation fails, revert back to the previous status
      if (context?.previousStatus) {
        queryClient.setQueryData(['indexerStatus'], context.previousStatus);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the correct data
      queryClient.invalidateQueries({ queryKey: ['indexerStatus'] });
    }
  });
};

/**
 * Hook for updating indexer configuration
 * @returns Mutation for updating indexer configuration
 */
export const useUpdateIndexerConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateIndexerConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['indexerConfig'] });
      queryClient.invalidateQueries({ queryKey: ['indexerStatus'] });
    }
  });
};

/**
 * Hook for fetching deposit detection metrics
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with deposit detection metrics
 */
export const useDepositDetectionMetrics = (refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['depositDetectionMetrics'],
    queryFn: fetchDepositDetectionMetrics,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for fetching recent deposits
 * @param limit Maximum number of deposits to return
 * @param offset Offset for pagination
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with recent deposits
 */
export const useRecentDeposits = (limit = 10, offset = 0, refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['recentDeposits', limit, offset],
    queryFn: () => fetchRecentDeposits(limit, offset),
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for fetching deposit statistics by blockchain
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with deposit statistics by blockchain
 */
export const useDepositStatsByBlockchain = (refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['depositStatsByBlockchain'],
    queryFn: fetchDepositStatsByBlockchain,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for fetching indexer performance metrics
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with indexer performance metrics
 */
export const useIndexerPerformanceMetrics = (refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['indexerPerformanceMetrics'],
    queryFn: fetchIndexerPerformanceMetrics,
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for fetching endpoint metrics
 * @param endpoint Optional endpoint path to filter by
 * @param method Optional HTTP method to filter by
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with endpoint metrics
 */
export const useEndpointMetrics = (endpoint?: string, method?: string, refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['endpointMetrics', endpoint, method],
    queryFn: () => fetchEndpointMetrics(endpoint, method),
    refetchInterval: refreshInterval,
    staleTime: refreshInterval * 0.9,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for fetching monitored addresses for a specific blockchain
 * @param blockchain Blockchain name
 * @param enabled Whether to enable the query
 * @returns Query result with monitored addresses
 */
export const useMonitoredAddresses = (blockchain: string, enabled = false) => {
  return useQuery({
    queryKey: ['monitoredAddresses', blockchain],
    queryFn: async () => {
      try {
        const response = await fetchMonitoredAddresses(blockchain);

        // Normalize the response structure to ensure it has the expected format
        // The API might return data in different formats
        let addresses = [];

        if (response && typeof response === 'object') {
          // Check all possible locations where addresses might be
          if (Array.isArray(response)) {
            // If response is directly an array
            addresses = response;
          } else if (Array.isArray(response.data)) {
            // If addresses are in response.data
            addresses = response.data;
          } else if (Array.isArray(response.addresses)) {
            // If addresses are in response.addresses
            addresses = response.addresses;
          } else if (response.data && typeof response.data === 'object' && Array.isArray(response.data.addresses)) {
            // If addresses are in response.data.addresses
            addresses = response.data.addresses;
          }
        }

        return {
          addresses: addresses
        };
      } catch (error) {
        console.error(`Error in useMonitoredAddresses hook for ${blockchain}:`, error);
        // In production, return an empty array rather than throwing
        return { addresses: [] };
      }
    },
    enabled: enabled && !!blockchain,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for reloading addresses for a specific blockchain
 * @returns Mutation for reloading addresses
 */
export const useReloadAddresses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (blockchain: string) => reloadAddresses(blockchain),
    onSuccess: (_, blockchain) => {
      // Invalidate the monitored addresses query for this blockchain
      queryClient.invalidateQueries({ queryKey: ['monitoredAddresses', blockchain] });

      // Also invalidate the indexer status to reflect any changes
      queryClient.invalidateQueries({ queryKey: ['indexerStatus'] });
    }
  });
};

/**
 * Hook for fetching Bloom filter configuration
 * @returns Query result with Bloom filter configuration
 */
export const useBloomFilterConfig = () => {
  return useQuery({
    queryKey: ['bloomFilterConfig'],
    queryFn: fetchBloomFilterConfig,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for updating Bloom filter configuration
 * @returns Mutation for updating Bloom filter configuration
 */
export const useUpdateBloomFilterConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: { bloomFilterThreshold?: number; bloomFilterRebuildThreshold?: number }) =>
      updateBloomFilterConfig(config),
    onSuccess: () => {
      // Invalidate the Bloom filter config query
      queryClient.invalidateQueries({ queryKey: ['bloomFilterConfig'] });
    }
  });
};

/**
 * Hook for fetching health status of all blockchain indexers
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with health status for all indexers
 */
export const useIndexerHealthStatus = (refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['indexerHealthStatus'],
    queryFn: () => fetchIndexerHealthStatus(),
    refetchInterval: refreshInterval,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for fetching health status of a specific blockchain indexer
 * @param blockchain Blockchain name (e.g., 'ethereum', 'bitcoin')
 * @param refreshInterval Interval in milliseconds to refresh data (default: 30000)
 * @returns Query result with health status for the specified indexer
 */
export const useBlockchainHealthStatus = (blockchain: string, refreshInterval = 30000) => {
  return useQuery({
    queryKey: ['blockchainHealthStatus', blockchain],
    queryFn: () => fetchBlockchainHealthStatus(blockchain),
    enabled: !!blockchain, // Only run the query if blockchain is provided
    refetchInterval: refreshInterval,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
};

/**
 * Hook for reprocessing a specific block for a blockchain
 * @returns Mutation for reprocessing a block
 */
export const useReprocessBlock = () => {
  return useMutation({
    mutationFn: ({ blockchain, blockNumber }: { blockchain: string; blockNumber: number }) =>
      reprocessBlock(blockchain, blockNumber)
  });
};
